import { PHOTOS } from "./constants";

// Full EN/ES translations for the Studio Setup Guide.
export const CONTENT = {
  en: {
    page_title: "Studio Setup Guide",
    page_sub:
      "Build a small or mid-size photography studio — equipment, lighting, backgrounds, and layouts.",
    download: "Download as PDF",
    generating: "Generating...",
    toc: "Table of Contents",
    sections: [
      "Space Planning",
      "Lighting Kit",
      "Triggers & Sync",
      "Light Meters",
      "Modifiers",
      "Backgrounds & Green Screen",
      "Support Gear",
      "Small vs Mid-Size Layouts",
      "Starter Checklists",
    ],
    section1: {
      title: "1. Space Planning",
      intro:
        "A good studio starts with the right space. Before buying gear, measure your room and plan how you will use it.",
      points: [
        "Small studio: 10 x 12 ft (3 x 3.6 m) minimum — enough for headshots, half-body portraits, and small products.",
        "Mid-size studio: 15 x 20 ft (4.5 x 6 m) — room for full-body shots, group portraits, and larger product work.",
        "Ceiling height: aim for 9 ft (2.7 m) or more. Low ceilings limit overhead lighting (hair lights, butterfly lighting).",
        "Flooring: hardwood or dark concrete reads clean. Avoid patterned carpet — it bounces color casts onto the subject.",
        "Outlets: plan for at least 4 grounded outlets on separate circuits. Strobes can trip breakers if everything runs on one line.",
        "Wall color: matte neutral gray or black absorbs bounce light and keeps color accurate.",
      ],
    },
    section2: {
      title: "2. Lighting Kit",
      intro:
        "Most studios use a 2-to-3-light system: Key (main), Fill (softens shadows), and Rim/Hair (separates subject from background).",
      cards: [
        {
          name: "Speedlights (Hotshoe Flashes)",
          img: PHOTOS.speedlight,
          desc: "Portable battery-powered flashes (Godox TT685, Canon 600EX, Yongnuo YN560). Best for mobile shoots, mid-power (60–100Ws).",
          price: "$80–$400 each",
        },
        {
          name: "Studio Strobes / Monolights",
          img: PHOTOS.strobe,
          desc: "AC-powered, high-output flashes (Godox AD600, Profoto B10). 200–600Ws, fast recycle, ideal for fashion/product work.",
          price: "$300–$1,800 each",
        },
        {
          name: "Continuous LED Panels",
          img: PHOTOS.led,
          desc: "What-you-see-is-what-you-get lighting (Aputure, Godox SL-60W). Great for beginners, video, and tethered product shots.",
          price: "$100–$900 each",
        },
      ],
      roles: [
        {
          label: "Key Light",
          text: "Primary light on the subject, typically 45° off-camera and slightly above. Sets the exposure.",
        },
        {
          label: "Fill Light",
          text: "Opposite side of key, 1–2 stops less powerful. Softens shadows. Can also be a reflector.",
        },
        {
          label: "Rim / Hair Light",
          text: "Behind the subject, aimed at the head/shoulders. Creates separation from the background.",
        },
      ],
    },
    section3: {
      title: "3. Triggers & Sync",
      intro:
        "Triggers fire your off-camera flashes wirelessly. Radio triggers are the modern standard — reliable over line-of-sight.",
      items: [
        "Radio triggers (recommended): Godox X-Pro, PocketWizard Plus IV, Profoto Air Remote. Work through walls, 100+ m range.",
        "Optical (slave) triggers: one flash fires when it sees another flash. Cheap but unreliable in bright sun or with multiple photographers.",
        "TTL vs Manual: TTL lets the camera set flash power automatically. Manual gives predictable, repeatable output — preferred in studio.",
        "HSS (High-Speed Sync): needed if shooting wide-open (f/1.8) in bright light at shutter speeds above 1/200s.",
        "Tip: stick to one brand's ecosystem (e.g., Godox X system) so one trigger controls all your flashes.",
      ],
    },
    section4: {
      title: "4. Light Meters",
      intro:
        "A handheld light meter measures light falling on the subject (incident metering) — far more accurate than the camera's reflective meter for controlled lighting.",
      items: [
        "Sekonic L-308X, L-478DR, or L-858D are industry standards.",
        "Set ISO + shutter → aim the white dome at the camera from the subject's position → fire the flash → meter shows the correct f-stop.",
        "Essential for consistent exposure when mixing multiple strobes at different power levels.",
        "Not mandatory for beginners — modern cameras + histogram can get you close — but a meter saves time and guesswork once you're working with paying clients.",
      ],
    },
    section5: {
      title: "5. Modifiers",
      intro:
        "Light shape and quality come from modifiers. Bigger + closer = softer. Smaller + farther = harder.",
      cards: [
        {
          name: "Softboxes",
          img: PHOTOS.softbox,
          desc: "Rectangular diffusers, 24–60 in. Soft, directional light. Great for headshots and product work.",
          price: "$40–$300",
        },
        {
          name: "Umbrellas",
          img: PHOTOS.umbrella,
          desc: "Shoot-through (diffuses) or reflective (bounces). Cheap, fast to set up, broader light spread than softboxes.",
          price: "$15–$80",
        },
        {
          name: "Beauty Dish",
          img: PHOTOS.beauty,
          desc: "16–22 in metal dish. Crisp, punchy light with defined shadows — favored for beauty and fashion portraits.",
          price: "$60–$300",
        },
        {
          name: "Reflectors",
          img: PHOTOS.reflector,
          desc: "5-in-1 collapsible discs (white, silver, gold, black, translucent). The cheapest light modifier and endlessly useful.",
          price: "$20–$60",
        },
      ],
    },
    section6: {
      title: "6. Backgrounds & Green Screen",
      intro:
        "The background defines the mood. Invest in a sturdy support system first; swap paper/fabric as budget allows.",
      cards: [
        {
          name: "Seamless Paper Rolls",
          img: PHOTOS.paper,
          desc: "9 ft or 53 in rolls (Savage Widetone). White, gray, black are must-haves. Roll down as it gets dirty — disposable.",
          price: "$45–$80 per roll",
        },
        {
          name: "Muslin / Canvas",
          img: PHOTOS.muslin,
          desc: "Washable fabric backdrops in solid colors or hand-painted textures. More portable, reusable, wrinkles easily.",
          price: "$30–$150",
        },
        {
          name: "Green Screen (Chroma Key)",
          img: PHOTOS.greenscreen,
          desc: "Bright chroma-green paper or fabric for post-production background replacement. Light it separately (2 flat lights) to avoid color spill.",
          price: "$40–$200",
        },
        {
          name: "Collapsible Backdrops",
          img: PHOTOS.collapsible,
          desc: "5 x 7 ft reversible pop-up (black/white or gray/chromakey). Perfect for small studios and on-location work.",
          price: "$50–$120",
        },
      ],
      greenscreen_tips: [
        "Subject stands at least 6 ft (2 m) in front of the green screen to avoid green spill on hair/skin.",
        "Light the background evenly with 2 flat lights at 45° — measure within 1/3 stop across the surface.",
        "Avoid green clothing. Avoid shiny jewelry or reflective glasses.",
        "Shoot at f/5.6 or narrower for sharper chroma key edges.",
      ],
    },
    section7: {
      title: "7. Support Gear",
      intro:
        "The unglamorous gear that makes a studio functional. Don't skimp on stands — a falling strobe destroys equipment and can injure people.",
      items: [
        "Light stands: air-cushioned, 8–10 ft. Minimum 2–3 stands for a basic setup.",
        "C-stands: heavier, steel construction with a gobo arm. For boom shots, hair lights, and holding modifiers above the subject.",
        "Sandbags: 15–25 lb each. One bag per stand is the rule. Prevents tipping.",
        "Boom arm: extends a light over the subject's head. Use with a C-stand and counterweight.",
        "Apple boxes: 20 x 12 x 8 in wooden boxes. Subjects sit, stand on them for height, or use as a product platform.",
        "Posing stool / backless chair: keeps subjects upright with good posture — flattering for portraits.",
        "Posing table: 24 x 32 in adjustable product table with white acrylic top for clean product shots.",
        "Gaffer tape, clamps, extension cords, power strips — budget $100 for these on day one.",
      ],
    },
    section9: {
      title: "9. Starter Checklists",
      small: {
        title: "Minimum Viable Studio (~$1,200)",
        items: [
          "2 Speedlights + radio trigger system",
          "2 Light stands + 2 sandbags",
          "1 Medium softbox (24 in) + 1 shoot-through umbrella",
          "5-in-1 reflector",
          "Background support + 9 ft seamless paper (white)",
          "Posing stool + apple box",
          "Gaffer tape + 25 ft extension cord",
        ],
      },
      mid: {
        title: "Mid-Size Studio (~$4,000–$6,000)",
        items: [
          "2 Monolights (400–600Ws) + 2 Speedlights as fill/rim",
          "Godox X-Pro or PocketWizard trigger system",
          "Sekonic light meter (L-308X or L-478DR)",
          "3–4 Light stands + 1 C-stand + boom arm",
          "1 Large softbox (48 in) + 1 medium softbox + beauty dish + 2 umbrellas",
          "5-in-1 reflector + black flag",
          "Background support + white/gray/black seamless paper + green screen",
          "Posing stool + posing table + 2 apple boxes",
          "4+ sandbags, gels, grids, clamps, gaffer tape",
        ],
      },
    },
    diagram: {
      small_title: "Small Studio Layout (10 x 12 ft)",
      mid_title: "Mid-Size Studio Layout (15 x 20 ft)",
      legend: {
        subject: "Subject",
        camera: "Camera",
        key: "Key Light",
        fill: "Fill Light",
        rim: "Rim / Hair Light",
        bg: "Background",
        bg_light: "Background Light",
      },
    },
    budget: {
      title: "Budget Tier Calculator",
      intro:
        "Tell us your budget and we'll prioritize what to buy first. The list is ordered so every dollar goes to the next most impactful piece of gear.",
      label: "Your Budget",
      preset_500: "$500",
      preset_1500: "$1,500",
      preset_4000: "$4,000",
      preset_8000: "$8,000",
      preset_12000: "$12,000",
      tier_label: "Recommended Tier",
      tiers: {
        essentials: "Essentials Only",
        starter: "Starter Studio",
        small: "Small Studio",
        mid: "Mid-Size Studio",
        advanced: "Advanced Studio",
        pro: "Full Pro Kit",
      },
      affordable: "Within Your Budget",
      stretch: "Save Up For Next",
      total_used: "Total Used",
      remaining: "Remaining",
      items_count: (n) => `${n} items`,
      items_left: (n) => `${n} items left to buy`,
      tip_essentials:
        "Focus on a single speedlight + reflector. You can do a lot with window light + one flash.",
      tip_starter:
        "Two lights unlocks key + fill. Get your first softbox — quality of light matters more than quantity.",
      tip_small:
        "A complete small portrait studio. You can shoot headshots, half-body, and small products professionally.",
      tip_mid:
        "Add monolights for faster recycle and real power. Invest in a Sekonic meter to dial in consistency.",
      tip_advanced:
        "Studio is complete — time to upgrade your post-production workflow. A calibrated monitor + color checker + Adobe CC transform your editing. Add a pro tripod and filter kit for location work.",
      tip_pro:
        "Everything you need to run a professional photo business: second camera body, 70–200 f/2.8 zoom, calibrated editing bay, drone, photo printer. Hit save and print this as your master purchase plan.",
      owned_label: "Owned",
      owned_title: "Already Owned",
      saved: "saved",
      signin_hint:
        "Sign in to save your shopping list across devices. Check off gear you already own and the calculator will prioritize what's left.",
      complete_label: "Full kit — every item fits your budget",
      checked_label: "Mark as not owned",
      uncheck_label: "Mark as owned",
    },
    footer_note:
      "A great studio is not about expensive gear — it is about understanding light. Start with the basics, shoot every day, and upgrade only when a specific limitation holds you back.",
  },
  es: {
    page_title: "Guía de Montaje de Estudio",
    page_sub:
      "Monta un estudio fotográfico pequeño o mediano — equipo, iluminación, fondos y disposición.",
    download: "Descargar como PDF",
    generating: "Generando...",
    toc: "Tabla de Contenido",
    sections: [
      "Planeación del Espacio",
      "Kit de Iluminación",
      "Disparadores y Sincronización",
      "Fotómetros",
      "Modificadores",
      "Fondos y Pantalla Verde",
      "Equipo de Soporte",
      "Montajes Pequeño vs Mediano",
      "Listas de Inicio",
    ],
    section1: {
      title: "1. Planeación del Espacio",
      intro:
        "Un buen estudio comienza con el espacio correcto. Antes de comprar equipo, mide el cuarto y planea como lo usarás.",
      points: [
        "Estudio pequeño: 3 x 3.6 m mínimo — suficiente para retratos de cabeza, medio cuerpo y productos pequeños.",
        "Estudio mediano: 4.5 x 6 m — espacio para cuerpo completo, retratos grupales y productos grandes.",
        "Altura del techo: 2.7 m o más. Techos bajos limitan la iluminación superior (luz de cabello, butterfly).",
        "Piso: madera o concreto oscuro se ve limpio. Evita alfombras con patrón — rebotan color al sujeto.",
        "Contactos: al menos 4 contactos aterrizados en circuitos separados. Los flashes de estudio pueden tumbar pastillas si todo va en una línea.",
        "Color de pared: gris neutro mate o negro absorbe luz rebotada y mantiene el color preciso.",
      ],
    },
    section2: {
      title: "2. Kit de Iluminación",
      intro:
        "La mayoría de los estudios usa un sistema de 2 o 3 luces: Principal (key), de Relleno (fill) y de Borde/Cabello (rim).",
      cards: [
        {
          name: "Speedlights (Flashes de Zapata)",
          img: PHOTOS.speedlight,
          desc: "Flashes portatiles con batería (Godox TT685, Canon 600EX, Yongnuo YN560). Ideales para movilidad, potencia media (60-100Ws).",
          price: "$80-$400 c/u",
        },
        {
          name: "Monolights / Flashes de Estudio",
          img: PHOTOS.strobe,
          desc: "Flashes de corriente AC de alta potencia (Godox AD600, Profoto B10). 200-600Ws, reciclaje rápido, ideales para moda/producto.",
          price: "$300-$1,800 c/u",
        },
        {
          name: "Paneles LED Continuos",
          img: PHOTOS.led,
          desc: "Iluminación WYSIWYG (Aputure, Godox SL-60W). Excelente para principiantes, video y tomas de producto conectadas.",
          price: "$100-$900 c/u",
        },
      ],
      roles: [
        {
          label: "Luz Principal (Key)",
          text: "Luz primaria sobre el sujeto, usualmente a 45° de la cámara y ligeramente elevada. Define la exposición.",
        },
        {
          label: "Luz de Relleno (Fill)",
          text: "Lado opuesto a la key, 1-2 pasos menos potente. Suaviza sombras. También puede ser un reflector.",
        },
        {
          label: "Luz de Borde / Cabello (Rim)",
          text: "Detrás del sujeto, apuntando a cabeza/hombros. Separa al sujeto del fondo.",
        },
      ],
    },
    section3: {
      title: "3. Disparadores y Sincronización",
      intro:
        "Los disparadores disparan tus flashes fuera de cámara de forma inalámbrica. Los de radio son el estándar moderno — confiables sin línea de vista.",
      items: [
        "Disparadores de radio (recomendados): Godox X-Pro, PocketWizard Plus IV, Profoto Air Remote. Funcionan a través de paredes, 100+ m.",
        "Disparadores ópticos (slave): un flash dispara al ver otro. Baratos pero poco confiables en sol directo o con múltiples fotógrafos.",
        "TTL vs Manual: TTL deja que la cámara decida la potencia. Manual da salida predecible y repetible — preferido en estudio.",
        "HSS (Sincronización de Alta Velocidad): necesario para disparar abierto (f/1.8) en luz brillante a velocidades sobre 1/200s.",
        "Consejo: quedate con el ecosistema de una marca (ej. sistema Godox X) para que un disparador controle todos tus flashes.",
      ],
    },
    section4: {
      title: "4. Fotómetros",
      intro:
        "Un fotómetro de mano mide la luz que cae sobre el sujeto (medición incidente) — mucho más preciso que el medidor reflejado de la cámara para iluminación controlada.",
      items: [
        "Sekonic L-308X, L-478DR o L-858D son los estándares de la industria.",
        "Ajusta ISO + velocidad → apunta la cupula blanca hacia la cámara desde la posición del sujeto → dispara el flash → el medidor muestra el f-stop correcto.",
        "Esencial para exposición consistente cuando mezclas múltiples flashes a diferentes potencias.",
        "No obligatorio para principiantes — cámara moderna + histograma te acercan — pero un fotómetro ahorra tiempo una vez trabajando con clientes de pago.",
      ],
    },
    section5: {
      title: "5. Modificadores",
      intro:
        "La forma y calidad de la luz viene de los modificadores. Más grande + más cerca = más suave. Más pequeño + más lejos = más duro.",
      cards: [
        {
          name: "Softboxes",
          img: PHOTOS.softbox,
          desc: "Difusores rectangulares, 24-60 pulg. Luz suave y direccional. Ideal para retratos y producto.",
          price: "$40-$300",
        },
        {
          name: "Sombrillas",
          img: PHOTOS.umbrella,
          desc: "Translucidas (difunden) o reflectivas (rebotan). Baratas, rápidas de armar, mayor difusión que softboxes.",
          price: "$15-$80",
        },
        {
          name: "Beauty Dish",
          img: PHOTOS.beauty,
          desc: "Plato metálico de 16-22 pulg. Luz definida con sombras marcadas — preferido en belleza y moda.",
          price: "$60-$300",
        },
        {
          name: "Reflectores",
          img: PHOTOS.reflector,
          desc: "Discos plegables 5 en 1 (blanco, plata, oro, negro, translucido). El modificador más barato e infinitamente útil.",
          price: "$20-$60",
        },
      ],
    },
    section6: {
      title: "6. Fondos y Pantalla Verde",
      intro:
        "El fondo define el ambiente. Invierte primero en un soporte resistente; cambia papel/tela según el presupuesto.",
      cards: [
        {
          name: "Rollos de Papel Seamless",
          img: PHOTOS.paper,
          desc: "Rollos de 2.7 m o 1.35 m (Savage Widetone). Blanco, gris y negro son indispensables. Desechable — lo enrollas cuando se ensucia.",
          price: "$45-$80 por rollo",
        },
        {
          name: "Muselina / Lona",
          img: PHOTOS.muslin,
          desc: "Telas lavables en colores solidos o texturas pintadas. Más portatiles, reusables, se arrugan fácil.",
          price: "$30-$150",
        },
        {
          name: "Pantalla Verde (Chroma Key)",
          img: PHOTOS.greenscreen,
          desc: "Papel o tela verde brillante para reemplazar el fondo en post-producción. Iluminala aparte (2 luces planas) para evitar contaminación.",
          price: "$40-$200",
        },
        {
          name: "Fondos Plegables",
          img: PHOTOS.collapsible,
          desc: "Pop-up reversible de 1.5 x 2.1 m (negro/blanco o gris/chroma). Ideal para estudios pequeños y locaciones.",
          price: "$50-$120",
        },
      ],
      greenscreen_tips: [
        "El sujeto debe estar al menos 2 m frente a la pantalla verde para evitar derrame de verde en cabello/piel.",
        "Ilumina el fondo uniformemente con 2 luces planas a 45° — medido dentro de 1/3 de paso en toda la superficie.",
        "Evita ropa verde. Evita joyeria brillante o lentes reflectivos.",
        "Dispara a f/5.6 o más cerrado para bordes chroma más nítidos.",
      ],
    },
    section7: {
      title: "7. Equipo de Soporte",
      intro:
        "El equipo poco glamoroso que hace funcional un estudio. No escatimes en trípodes — un flash cayendo destruye equipo y puede lesionar.",
      items: [
        "Trípodes de luz: con amortiguación de aire, 2.4-3 m. Mínimo 2-3 trípodes para un montaje básico.",
        "C-stands: más pesados, acero con brazo gobo. Para luces en boom, luces de cabello y sostener modificadores sobre el sujeto.",
        "Sandbags: 7-11 kg c/u. Una bolsa por trípode es la regla. Previene caídas.",
        "Boom arm: extiende una luz sobre la cabeza del sujeto. Usar con C-stand y contrapeso.",
        "Apple boxes: cajas de madera 50 x 30 x 20 cm. Para que los sujetos se sienten, ganen altura o como plataforma de producto.",
        "Banco / silla sin respaldo: mantiene al sujeto erguido con buena postura — favorece los retratos.",
        "Mesa de posado: 60 x 80 cm ajustable con tapa de acrilico blanco para tomas de producto limpias.",
        "Cinta gaffer, clamps, extensiones, multicontactos — presupuesta $100 para esto desde el primer día.",
      ],
    },
    section9: {
      title: "9. Listas de Inicio",
      small: {
        title: "Estudio Mínimo Viable (~$1,200)",
        items: [
          "2 Speedlights + sistema de disparador de radio",
          "2 Trípodes de luz + 2 sandbags",
          "1 Softbox medio (60 cm) + 1 sombrilla translucida",
          "Reflector 5 en 1",
          "Soporte de fondo + papel seamless 2.7 m (blanco)",
          "Banco de posado + apple box",
          "Cinta gaffer + extensión de 7.5 m",
        ],
      },
      mid: {
        title: "Estudio Mediano (~$4,000-$6,000)",
        items: [
          "2 Monolights (400-600Ws) + 2 Speedlights para fill/rim",
          "Sistema Godox X-Pro o PocketWizard",
          "Fotómetro Sekonic (L-308X o L-478DR)",
          "3-4 Trípodes + 1 C-stand + boom arm",
          "1 Softbox grande (120 cm) + 1 softbox medio + beauty dish + 2 sombrillas",
          "Reflector 5 en 1 + bandera negra",
          "Soporte + papel seamless blanco/gris/negro + pantalla verde",
          "Banco + mesa de posado + 2 apple boxes",
          "4+ sandbags, geles, grids, clamps, cinta gaffer",
        ],
      },
    },
    diagram: {
      small_title: "Montaje Estudio Pequeño (3 x 3.6 m)",
      mid_title: "Montaje Estudio Mediano (4.5 x 6 m)",
      legend: {
        subject: "Sujeto",
        camera: "Cámara",
        key: "Luz Principal",
        fill: "Luz de Relleno",
        rim: "Luz de Borde/Cabello",
        bg: "Fondo",
        bg_light: "Luz de Fondo",
      },
    },
    budget: {
      title: "Calculadora de Presupuesto",
      intro:
        "Dinos tu presupuesto y te daremos prioridad a que comprar primero. La lista esta ordenada para que cada dólar vaya al siguiente equipo de mayor impacto.",
      label: "Tu Presupuesto",
      preset_500: "$500",
      preset_1500: "$1,500",
      preset_4000: "$4,000",
      preset_8000: "$8,000",
      preset_12000: "$12,000",
      tier_label: "Nivel Recomendado",
      tiers: {
        essentials: "Solo lo Esencial",
        starter: "Estudio Inicial",
        small: "Estudio Pequeño",
        mid: "Estudio Mediano",
        advanced: "Estudio Avanzado",
        pro: "Kit Pro Completo",
      },
      affordable: "Dentro de tu Presupuesto",
      stretch: "Ahorra para lo Siguiente",
      total_used: "Total Usado",
      remaining: "Restante",
      items_count: (n) => `${n} artículos`,
      items_left: (n) => `${n} artículos pendientes`,
      tip_essentials:
        "Enfocate en un solo speedlight + reflector. Puedes hacer mucho con luz de ventana + un flash.",
      tip_starter:
        "Dos luces habilitan key + fill. Consigue tu primer softbox — la calidad de la luz importa más que la cantidad.",
      tip_small:
        "Un estudio de retrato pequeño completo. Puedes hacer cabezas, medio cuerpo y productos pequeños profesionalmente.",
      tip_mid:
        "Agrega monolights para reciclaje rápido y potencia real. Invierte en un fotómetro Sekonic para consistencia.",
      tip_advanced:
        "El estudio esta completo — ahora mejora tu flujo de post-producción. Un monitor calibrado + color checker + Adobe CC transforman tu edición. Agrega trípode pro y kit de filtros para sesiones en locación.",
      tip_pro:
        "Todo lo necesario para un negocio fotográfico profesional: segundo cuerpo de cámara, zoom 70–200 f/2.8, estación de edición calibrada, drone, impresora fotográfica. Guarda e imprime esto como tu plan maestro de compras.",
      owned_label: "Tienes",
      owned_title: "Ya Tienes",
      saved: "ahorrado",
      signin_hint:
        "Inicia sesión para guardar tu lista de compras entre dispositivos. Marca el equipo que ya tienes y la calculadora priorizara lo que te falta.",
      complete_label: "Kit completo — todo cabe en tu presupuesto",
      checked_label: "Quitar de 'ya tengo'",
      uncheck_label: "Marcar como 'ya tengo'",
    },
    footer_note:
      "Un gran estudio no se trata de equipo caro — se trata de entender la luz. Empieza con lo básico, dispara todos los días, y mejora solo cuando una limitación especifica te detenga.",
  },
};
